import type { MouseEvent } from 'react';
import { twMerge } from 'tailwind-merge';
import CircularProgress from '@mui/material/CircularProgress';
import { CheckGlyphIcon, IndeterminateGlyphIcon } from '@/icons';
import { classes } from './utils';

export type CheckBoxProps<N extends string> = {
  isUpdating?: boolean;
  isChecked?: boolean;
  variant?: 'light' | 'dark';
  className?: string;
  name: N;
  onChange: (updates: Record<N, boolean>) => void;
};

export default function CheckBox<N extends string>({
  isUpdating,
  isChecked,
  variant = 'dark',
  className,
  name,
  onChange,
}: CheckBoxProps<N>) {
  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onChange({ [name]: !isChecked } as Record<N, boolean>);
  };

  if (isUpdating) {
    return (
      <span className={twMerge(classes.loader, className)}>
        <CircularProgress size={13} color="grey-5" />
      </span>
    );
  }

  const isIndeterminate = isChecked === undefined;
  const isFilled = isIndeterminate || isChecked;

  return (
    <button
      className={twMerge(
        classes.box,
        isFilled ? classes[variant] : classes.unchecked,
        className
      )}
      type="button"
      onClick={handleClick}>
      {isChecked && <CheckGlyphIcon className={classes.glyph} />}
      {isIndeterminate && <IndeterminateGlyphIcon className={classes.glyph} />}
    </button>
  );
}
